import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { KeyRoundIcon, PlusIcon } from 'lucide-react';
import { API_KEY_SCOPES, PERMISSIONS, type ApiKeyScope } from '@siteflow/shared';
import { ConfirmDialog } from '@/components/confirm-dialog';
import { CopyField } from '@/components/copy-field';
import { EmptyState } from '@/components/empty-state';
import { PermissionGate } from '@/components/permission-gate';
import { StatusChip } from '@/components/status-chip';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { messageForError } from '@/lib/api-client';
import { siteApiKeysQuery, siteKeys, sitesApi } from '@/lib/api/sites';
import type { CreatedApiKey, SiteApiKey } from '@/lib/api/types';
import { formatDate, formatDateTime } from '@/lib/utils';

const SCOPE_COPY: Record<ApiKeyScope, string> = {
  [API_KEY_SCOPES.CONTENT_READ]: 'Read published and draft content, and the media library.',
  [API_KEY_SCOPES.CONTENT_WRITE]: 'Create and update draft entries.',
  [API_KEY_SCOPES.REGISTRY_READ]: 'Read the site and its registered content keys.',
  [API_KEY_SCOPES.MEDIA_WRITE]: 'Upload, edit and delete media assets.',
  [API_KEY_SCOPES.PUBLISH]: 'Publish drafts to an environment.',
};

const SCOPES = Object.values(API_KEY_SCOPES);

function keyStatus(apiKey: SiteApiKey): { tone: 'active' | 'danger' | 'pending'; label: string } {
  if (apiKey.revokedAt) return { tone: 'danger', label: 'Revoked' };
  if (apiKey.expiresAt && new Date(apiKey.expiresAt) < new Date()) {
    return { tone: 'danger', label: 'Expired' };
  }
  if (!apiKey.lastUsedAt) return { tone: 'pending', label: 'Never used' };
  return { tone: 'active', label: 'Active' };
}

function ApiKeysTab() {
  const { orgId, siteId } = Route.useParams();
  const queryClient = useQueryClient();

  const apiKeys = useQuery(siteApiKeysQuery(orgId, siteId));

  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState('');
  const [expiresInDays, setExpiresInDays] = useState('');
  const [scopes, setScopes] = useState<string[]>([API_KEY_SCOPES.CONTENT_READ]);
  const [created, setCreated] = useState<CreatedApiKey | null>(null);
  const [revoking, setRevoking] = useState<SiteApiKey | null>(null);

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: siteKeys.apiKeys(orgId, siteId) });
  }

  const createMutation = useMutation({
    mutationFn: () =>
      sitesApi.createApiKey(orgId, siteId, {
        name: name.trim(),
        scopes,
        ...(expiresInDays ? { expiresInDays: Number(expiresInDays) } : {}),
      }),
    onSuccess: (apiKey) => {
      setCreated(apiKey);
      setCreateOpen(false);
      setName('');
      setExpiresInDays('');
      setScopes([API_KEY_SCOPES.CONTENT_READ]);
      invalidate();
    },
    onError: (error) => toast.error(messageForError(error, 'That key could not be created.')),
  });

  const revokeMutation = useMutation({
    mutationFn: (apiKeyId: string) => sitesApi.revokeApiKey(orgId, siteId, apiKeyId),
    onSuccess: () => {
      toast.success('Key revoked');
      setRevoking(null);
      invalidate();
    },
    onError: (error) => toast.error(messageForError(error, 'That key could not be revoked.')),
  });

  function toggleScope(scope: string) {
    setScopes((current) =>
      current.includes(scope) ? current.filter((item) => item !== scope) : [...current, scope],
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-prose">
          <h2 className="text-lede font-semibold tracking-tight text-ink">API keys</h2>
          <p className="text-data leading-relaxed text-ink-muted">
            Server-side credentials for build pipelines and integrations. Unlike the public key,
            these carry write access, so they belong in a secret store — never in front-end code.
          </p>
        </div>
        <PermissionGate permission={PERMISSIONS.API_KEYS_MANAGE}>
          <Button onClick={() => setCreateOpen(true)}>
            <PlusIcon />
            Create key
          </Button>
        </PermissionGate>
      </div>

      {apiKeys.isLoading ? (
        <div className="flex flex-col gap-2" aria-busy="true">
          {Array.from({ length: 3 }, (_, index) => (
            <Skeleton key={index} className="h-14 w-full" />
          ))}
        </div>
      ) : null}

      {apiKeys.isError ? (
        <p role="alert" className="text-data text-danger">
          {messageForError(apiKeys.error, 'The key list could not be loaded.')}
        </p>
      ) : null}

      {!apiKeys.isLoading && (apiKeys.data?.length ?? 0) === 0 && !apiKeys.isError ? (
        <EmptyState
          icon={KeyRoundIcon}
          title="No API keys yet"
          description="An API key lets something other than a person talk to this site — a CI job publishing after a deploy, or a script importing copy. The secret is shown once at creation and only its hash is kept."
          action={
            <PermissionGate permission={PERMISSIONS.API_KEYS_MANAGE}>
              <Button onClick={() => setCreateOpen(true)}>Create the first key</Button>
            </PermissionGate>
          }
        />
      ) : null}

      {(apiKeys.data?.length ?? 0) > 0 ? (
        <ul className="flex flex-col rounded-lg border border-hairline bg-elevated">
          {(apiKeys.data ?? []).map((apiKey) => {
            const status = keyStatus(apiKey);
            return (
              <li
                key={apiKey.id}
                className="flex flex-wrap items-center gap-3 border-b border-hairline px-4 py-3 last:border-b-0"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-data font-medium text-ink">{apiKey.name}</p>
                  <p className="font-mono text-micro text-ink-subtle">{apiKey.keyPrefix}…</p>
                  <p className="text-micro text-ink-subtle" data-numeric>
                    Created {formatDate(apiKey.createdAt)}
                    {apiKey.expiresAt ? ` · expires ${formatDate(apiKey.expiresAt)}` : ''}
                    {apiKey.lastUsedAt ? ` · last used ${formatDateTime(apiKey.lastUsedAt)}` : ''}
                  </p>
                </div>
                <div className="flex flex-wrap gap-1">
                  {apiKey.scopes.map((scope) => (
                    <Badge key={scope} variant="outline" className="font-mono">
                      {scope}
                    </Badge>
                  ))}
                </div>
                <StatusChip tone={status.tone}>{status.label}</StatusChip>
                {!apiKey.revokedAt ? (
                  <PermissionGate permission={PERMISSIONS.API_KEYS_MANAGE}>
                    <Button variant="outlineDanger" size="sm" onClick={() => setRevoking(apiKey)}>
                      Revoke
                    </Button>
                  </PermissionGate>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : null}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create an API key</DialogTitle>
            <DialogDescription>
              Grant the narrowest set of scopes the integration actually needs. The secret is shown
              once and cannot be retrieved afterwards.
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="key-name">Name</Label>
              <Input
                id="key-name"
                value={name}
                placeholder="Deploy pipeline"
                onChange={(event) => setName(event.target.value)}
              />
            </div>

            <fieldset className="flex flex-col gap-2">
              <legend className="section-label pb-1">Scopes</legend>
              {SCOPES.map((scope) => (
                <label key={scope} className="flex items-start gap-2.5">
                  <Checkbox
                    checked={scopes.includes(scope)}
                    onCheckedChange={() => toggleScope(scope)}
                    aria-describedby={`scope-${scope}`}
                  />
                  <span className="flex flex-col gap-0.5">
                    <span className="font-mono text-data text-ink">{scope}</span>
                    <span id={`scope-${scope}`} className="text-micro text-ink-subtle">
                      {SCOPE_COPY[scope]}
                    </span>
                  </span>
                </label>
              ))}
            </fieldset>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="key-expiry">Expires after (days)</Label>
              <Input
                id="key-expiry"
                type="number"
                min={1}
                max={3650}
                value={expiresInDays}
                placeholder="Leave empty for no expiry"
                onChange={(event) => setExpiresInDays(event.target.value)}
              />
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button
              disabled={!name.trim() || scopes.length === 0 || createMutation.isPending}
              onClick={() => createMutation.mutate()}
            >
              {createMutation.isPending ? 'Creating…' : 'Create key'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={created !== null} onOpenChange={(open) => !open && setCreated(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Copy this key now</DialogTitle>
            <DialogDescription>
              This is the only time the secret is shown. The server stores a hash of it, so it
              cannot be recovered — if you lose it, revoke this key and create another.
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="flex flex-col gap-3">
            <CopyField label={created?.name ?? 'API key'} value={created?.key ?? ''} truncate={false} />
            <p className="rounded-md border border-warning/35 bg-warning-soft px-3 py-2 text-micro leading-relaxed text-warning">
              Treat it like a password: store it in your deployment platform's secret manager, not
              in the repository.
            </p>
          </DialogBody>
          <DialogFooter>
            <Button onClick={() => setCreated(null)}>I have saved it</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={revoking !== null}
        onOpenChange={(open) => !open && setRevoking(null)}
        destructive
        title="Revoke this key?"
        confirmLabel="Revoke key"
        pending={revokeMutation.isPending}
        description={
          <>
            <strong className="text-ink">{revoking?.name}</strong> stops working immediately.
            Anything still using it — a deploy pipeline, an import script — starts failing on its
            next request. This cannot be undone.
          </>
        }
        onConfirm={() => revoking && revokeMutation.mutate(revoking.id)}
      />
    </div>
  );
}

export const Route = createFileRoute('/app/$orgId/sites/$siteId/api-keys')({
  component: ApiKeysTab,
});
